import debounce from "lodash/debounce";
import { IMapOptions } from "../../business-objects/MapOptions";
import { ICoordinates } from "../../business-objects/AlacrityInterfaces";
import MapComponents from "../../business-objects/MapComponents";
import { esriModules, mapReadyProps } from "../MapReady";
import { ICoordinatesExtended } from "../../business-objects/ExtendedCoordinates";
import { ILocationData } from "../../business-objects/LocationData";
const ActiveImage = require("../../../../../../assets/Active.svg");
const Entry  = require("../../../../../../assets/Entry.svg");
const Investigator  = require("../../../../../../assets/Investigator.svg");

/*
const activeSymbol = {
  type: "simple-marker",
  size: "8pt",
  color:'red',
  outline: {
    color: "black"
  }
};

const dataEntrySymbol = {
  type: "simple-marker",
  size: "8pt",
  color:'maroon',
  outline: {
    color: "black"
  }
};


const investigatorSymbol = {
  type: "simple-marker",
  size: "8pt",
  color:'black',
  outline: {
    color: "red"
  }
};

*/

const activeSymbol = {
  type:"picture-marker",
  url: ActiveImage,
  width:"16px",
  height:"17.5px",
  yoffset:"10px"
};


const dataEntrySymbol = {
  type:"picture-marker",
  url: Entry,
  width:"16px",
  height:"17.5px",
  yoffset:"10px"
};


const investigatorSymbol = {
  type:"picture-marker",
  url: Investigator,
  width:"16px",
  height:"17.5px",
  yoffset:"8px"
};


const getFormModules = (mapReadyProps: mapReadyProps): esriModules => {
  const [
    Map,
    MapView,
    FeatureLayer,
    BasemapToggle,
    watchUtils,
    Graphic,
    PictureMarkerSymbol,
    Point,
    Search,
    GraphicsLayer,
    Sketch,
    WebMercatorUtils,
    Locator,
    LayerList,
    BaseMap
  ] = mapReadyProps.loadedModules;

  return {
    Map,
    MapView,
    FeatureLayer,
    BasemapToggle,
    watchUtils,
    Graphic,
    PictureMarkerSymbol,
    Point,
    Search,
    GraphicsLayer,
    Sketch,
    WebMercatorUtils,
    Locator,
    LayerList,
    BaseMap
  };
};

/**
 * @description Builds the ESRI map components used in the form and sets up the mapComponents interface to obfuscate the ESRI functions from the caller.
 * @param {mapReadyProps} mapReadyProps
 * @returns {MapComponents}
 */
export const buildFormMap = (
  mapReadyProps: mapReadyProps,
  mapLayers: any,
  geocodeServer: string,
  isNightmode: boolean
): MapComponents => {
  const modules = getFormModules(mapReadyProps);

   const hybridBaseMap = new modules.BaseMap({
    portalItem: {
      id: "86265e5a4bbb4187a59719cf134e0018",
    },
    thumbnailUrl: "https://js.arcgis.com/4.13/esri/images/basemap/hybrid.jpg",
  });

  const map = new modules.Map({
    basemap: hybridBaseMap,
  });

  const getPointRenderer = () => {
    return {
      type: "simple",
      symbol: {
        type: "simple-marker",
        style: "square",
        size: "10px",
        color: "red",
        outline: {
          color: "red",
          width: 2,
        },
      },
    };
  };

  const getLineRenderer = () => {
    return {
      type: "simple",
      symbol: {
        type: "simple-fill",
        color: "transparent",
        outline: {
          width: 2.0,
          color: "purple",
        },
      },
    };
  };

  
  const lineRenderer = getLineRenderer();
  const pointRenderer = getPointRenderer();
  
  let featureLayers: any[] = [
    new modules.FeatureLayer({ url: "https://services8.arcgis.com/ovrd81pb0zC79CeU/arcgis/rest/services/LRS_ReferencePosts_DOT/FeatureServer", 
      outFields: ["*"], 
      visible: false, 
      title: "Reference Posts",
      renderer: pointRenderer }),
    new modules.FeatureLayer({ url: "https://services8.arcgis.com/ovrd81pb0zC79CeU/arcgis/rest/services/HighwaysMajorMinorsLocalRamps_RefPostRoutes_7_25/FeatureServer", 
      outFields: ["*"], 
      title: "All Roads", 
      listMode: "hide",
      renderer: lineRenderer}),
    new modules.FeatureLayer({ url: "https://services8.arcgis.com/ovrd81pb0zC79CeU/arcgis/rest/services/BND_CountyBoundary_DOT/FeatureServer", outFields: ["*"], visible: false, title: "County Boundaries" }),
    new modules.FeatureLayer({ url: "https://services8.arcgis.com/ovrd81pb0zC79CeU/arcgis/rest/services/BND_CityBoundary_DOT/FeatureServer", outFields: ["*"], title: "City Boundaries" })
  ];

  /*
  (mapLayers || []).forEach(mapLayer =>
    featureLayers.push(
      new modules.FeatureLayer({ url: mapLayer.featureLayerUrl, outFields: ["*"], minScale: mapLayer.minScale })
    )
  );
*/
  featureLayers.forEach(layer => {
    map.layers.add(layer);
  });

  const view = new modules.MapView({
    container: mapReadyProps.containerNode,
    map: map,
    zoom:15
  });

  const locator = new modules.Locator({ url: "https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer" }); //geocodeServer

  const searchWidget = new modules.Search({
    view: view,
    popupEnabled: false,
    resultGraphicEnabled: false,
    activeSourceIndex: -1,
    maxResults: 100,
    maxSuggestions: 100,
    autoSelect: false,
    includeDefaultSources: false,
    sources: [
      {
        locator: locator,
        singleLineFieldName: "SingleLine",
        name: "ArcGIS World Geocoding Service",
        placeholder: "Enter an address",
        outFields: ["*"],
        searchTemplate: "{Match_addr}",
        localSearchOptions: {
          minScale: 30000,
          distance: 5000
        }
      }
    ]
  });

  const basemapToggle = new modules.BasemapToggle({
    view: view,
    nextBasemap: 'streets-vector',
  });

  const layerList = new modules.LayerList({
    view
  });

  view.when(() => {
    view.ui.add(basemapToggle, "top-right");

    /*view.ui.add(searchWidget, {
      position: "top-left",
      index: 2
    });*/

    view.ui.add(layerList, "top-right");

    view.popup.actions = [];
    view.popup.dockEnabled = false;
    view.popup.dockOptions = {
      breakpoint: false,
      buttonEnabled: false,
      position: "auto"
    };
    view.popup.collapseEnabled = false;
    view.popup.spinnerEnabled = true;
    view.popup.autoOpenEnabled = false;

  });

  let graphic = null;

  const removeGraphic = () => {
    if (graphic) {
      view.when(() => view.graphics.remove(graphic)).then(() => (graphic = null));
    }
  };

  const setGraphic = ({ longitude, latitude }: ICoordinates | ICoordinatesExtended, pointToRemove:any) => {
    const point = new modules.Point({
      longitude,
      latitude
    });

    if (graphic) {
      graphic.geometry = point;
    } else {
      graphic = new modules.Graphic({
        symbol: activeSymbol,
        geometry: point
      });

      view.when(() => {
        if (pointToRemove)
          view.graphics.remove(view.graphics.find(a => a.geometry.latitude == pointToRemove.latitude && a.geometry.longitude == pointToRemove.longitude));
        view.graphics.add(graphic);
      });
    }
  };

  const setMultipoints = (items: ILocationData[], currentUserId: number) => {
    view.when(() => {
      items.map((data,index) => {
        let point = new modules.Point({
          longitude: data.longitude,
          latitude: data.latitude
        });

        let grap = new modules.Graphic({
          symbol: (data.userTypeId == 1) ? investigatorSymbol : ((data.userTypeId == 2) ? dataEntrySymbol : activeSymbol),
          geometry: point
        });

        view.graphics.add(grap);
      });
      view.center = items[items.length - 1];
    })
  }

  const onCenterPointUpdated = (callback: (longitude: number, latitude: number, zoomLevel: number) => void): void => {
    view.when(() => {
      modules.watchUtils.when(
        view,
        "stationary",
        debounce(() => {
          callback(view.center.longitude, view.center.latitude, view.zoom);
        }, 1000)
      );
    });
  };

  return {
    map,
    featureLayers,
    popup: view.popup,
    view,
    basemapToggle,
    setGraphic,
    removeGraphic,
    onCenterPointUpdated,
    point: modules.Point,
    searchWidget,
    locator,
    webMercatorUtils: modules.WebMercatorUtils,
    setMultipoints
  };
};

export const formModulesToLoad = [
  "esri/Map",
  "esri/views/MapView",
  "esri/layers/FeatureLayer",
  "esri/widgets/BasemapToggle",
  "esri/core/watchUtils",
  "esri/Graphic",
  "esri/symbols/PictureMarkerSymbol",
  "esri/geometry/Point",
  "esri/widgets/Search",
  "esri/layers/GraphicsLayer",
  "esri/widgets/Sketch",
  "esri/geometry/support/webMercatorUtils",
  "esri/tasks/Locator",
  "esri/widgets/LayerList",
  "esri/Basemap"
];
